import {
  Component,
  OnDestroy,
  OnChanges,
  AfterViewInit,
  forwardRef,
  ChangeDetectorRef,
  Input,
  Output,
  EventEmitter,
  ContentChild,
  TemplateRef,
  ViewEncapsulation,
  HostListener,
  HostBinding,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
  Inject,
  SimpleChanges,
  ContentChildren,
  QueryList,
  InjectionToken,
  NgZone,
  Attribute
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  takeUntil,
  startWith,
  tap,
  debounceTime,
  map,
  filter
} from 'rxjs/operators';
import { merge } from 'rxjs/observable/merge';
import { Subject } from 'rxjs/Subject';

import {
  NgOptionTemplateDirective,
  NgLabelTemplateDirective,
  NgHeaderTemplateDirective,
  NgFooterTemplateDirective,
  NgOptgroupTemplateDirective,
  NgNotFoundTemplateDirective,
  NgTypeToSearchTemplateDirective,
  NgLoadingTextTemplateDirective,
  NgMultiLabelTemplateDirective,
  NgTagTemplateDirective
} from './ng-templates.directive';

import { ConsoleService } from './console.service';
import { isDefined, isFunction, isPromise, isObject } from './value-utils';
import { ItemsList } from './items-list';
import { NgOption, KeyCode, NgSelectConfig } from './ng-select.types';
import { newId } from './id';
import { NgDropdownPanelComponent } from './ng-dropdown-panel.component';
import { NgOptionComponent } from './ng-option.component';
import { WindowService } from './window.service';

export const NG_SELECT_DEFAULT_CONFIG = new InjectionToken<NgSelectConfig>(
  'ng-select-default-options'
);
export type DropdownPosition = 'bottom' | 'top' | 'auto';
export type AddTagFn = (term: string) => any | Promise<any>;
export type CompareWithFn = (a: any, b: any) => boolean;

@Component({
  selector: 'ng-select',
  template: `<div (mousedown)="handleMousedown($event)" [class.ng-has-value]="hasValue" class="ng-select-container">
    <div class="ng-value-container">
        <div class="ng-placeholder">{{placeholder}}</div>

        <ng-container *ngIf="!multiLabelTemplate && selectedItems.length > 0">
            <div [class.ng-value-disabled]="item.disabled" class="ng-value" *ngFor="let item of selectedItems">
                <ng-template #defaultLabelTemplate>
                    <span class="ng-value-icon left" (click)="unselect(item);" aria-hidden="true">×</span>
                    <span #labelSpanRef class="ng-value-label">{{item.label}}</span>
                </ng-template>

                <ng-template
                    [ngTemplateOutlet]="labelTemplate || defaultLabelTemplate"
                    [ngTemplateOutletContext]="{ item: item.value, clear: clearItem, label: item.label }">
                </ng-template>
            </div>
        </ng-container>

        <ng-template *ngIf="multiLabelTemplate && selectedValues.length > 0"
                [ngTemplateOutlet]="multiLabelTemplate"
                [ngTemplateOutletContext]="{ items: selectedValues, clear: clearItem }">
        </ng-template>

        <div class="ng-input">
            <input #filterInput
                   type="text"
                   autocomplete="{{dropdownId}}"
                   [id]="labelForId"
                   [readOnly]="!searchable"
                   [disabled]="isDisabled"
                   [(ngModel)]="filterValue"
                   (input)="filter()"
                   (focus)="onInputFocus()"
                   (blur)="onInputBlur()"
                   (keyup)="searchEvent.emit($event.target.value)"
                   (change)="$event.stopPropagation()"
                   role="combobox"
                   [attr.aria-expanded]="isOpen"
                   [attr.aria-owns]="isOpen ? dropdownId : null"
                   [attr.aria-activedescendant]="isOpen ? itemsList?.markedItem?.htmlId : null">
        </div>
    </div>

    <div class="ng-spinner-loader" *ngIf="loading"></div>

    <span *ngIf="showClear()" class="ng-clear-wrapper" title="{{clearAllText}}">
        <span class="ng-clear" aria-hidden="true">×</span>
    </span>

    <span class="ng-arrow-wrapper">
        <span class="ng-arrow"></span>
    </span>
</div>

<ng-dropdown-panel *ngIf="isOpen"
    class="ng-dropdown-panel"
    [virtualScroll]="virtualScroll"
    [bufferAmount]="bufferAmount"
    [appendTo]="appendTo"
    [position]="dropdownPosition"
    [headerTemplate]="headerTemplate"
    [footerTemplate]="footerTemplate"
    [items]="itemsList.filteredItems"
    (update)="viewPortItems = $event"
    (scrollToEnd)="scrollToEnd.emit($event)"
    (outsideClick)="close(true)"
    (mouseout)="itemsList.unmarkItem()"
    [class.ng-select-multiple]="multiple"
    [ngClass]="classes"
    [id]="dropdownId">

    <ng-container>
        <div class="ng-option" [attr.role]="item.hasChildren ? 'group' : 'option'" (click)="toggleItem(item)" (mousedown)="$event.preventDefault()" (mouseover)="onItemHover(item)"
                *ngFor="let item of viewPortItems"
                [class.ng-option-disabled]="item.disabled"
                [class.ng-option-selected]="item.selected"
                [class.ng-optgroup]="item.hasChildren"
                [class.ng-option]="!item.hasChildren"
                [class.ng-option-child]="!!item.parent"
                [class.ng-option-marked]="item === itemsList.markedItem"
                id="{{item?.htmlId || null}}">

            <ng-template #defaultOptionTemplate>
                <span class="ng-option-label">{{item.label}}</span>
            </ng-template>

            <ng-template
                [ngTemplateOutlet]="item.hasChildren ? (optgroupTemplate || defaultOptionTemplate) : (optionTemplate || defaultOptionTemplate)"
                [ngTemplateOutletContext]="{ item: item.value, index: item.index, searchTerm: filterValue }">
            </ng-template>
        </div>

        <div class="ng-option" [class.ng-option-marked]="addItem" (mouseover)="selectAddTag()" (mouseout)="unSelectAddTag()" role="option" (click)="selectTag()" *ngIf="showAddTag()">
            <ng-template #defaultTagTemplate>
                <span><span class="ng-tag-label">{{addTagText}}</span>"{{filterValue}}"</span>
            </ng-template>

            <ng-template
                [ngTemplateOutlet]="tagTemplate || defaultTagTemplate"
                [ngTemplateOutletContext]="{ searchTerm: filterValue }">
            </ng-template>
        </div>
    </ng-container>

    <ng-container *ngIf="showNoItemsFound()">
        <ng-template #defaultNotFoundTemplate>
            <div class="ng-option ng-option-disabled">{{notFoundText}}</div>
        </ng-template>

        <ng-template
            [ngTemplateOutlet]="notFoundTemplate || defaultNotFoundTemplate"
            [ngTemplateOutletContext]="{ searchTerm: filterValue }">
        </ng-template>
    </ng-container>

    <ng-container *ngIf="showTypeToSearch()">
        <ng-template #defaultTypeToSearchTemplate>
            <div class="ng-option ng-option-disabled">{{typeToSearchText}}</div>
        </ng-template>

        <ng-template
            [ngTemplateOutlet]="typeToSearchTemplate || defaultTypeToSearchTemplate">
        </ng-template>
    </ng-container>

    <ng-container *ngIf="loading && itemsList.filteredItems.length === 0">
        <ng-template #defaultLoadingTextTemplate>
            <div class="ng-option ng-option-disabled">{{loadingText}}</div>
        </ng-template>

        <ng-template
            [ngTemplateOutlet]="loadingTextTemplate || defaultLoadingTextTemplate"
            [ngTemplateOutletContext]="{ searchTerm: filterValue  }">
        </ng-template>
    </ng-container>

</ng-dropdown-panel>
`,
  styles: [`.ng-select{position:relative;display:block;-webkit-box-sizing:border-box;box-sizing:border-box}.ng-select div,.ng-select input,.ng-select span{-webkit-box-sizing:border-box;box-sizing:border-box}.ng-select [hidden]{display:none}.ng-select.ng-select-searchable .ng-select-container .ng-value-container .ng-input{opacity:1}.ng-select.ng-select-opened .ng-select-container{z-index:1001}.ng-select.ng-select-disabled .ng-select-container .ng-value-container .ng-placeholder,.ng-select.ng-select-disabled .ng-select-container .ng-value-container .ng-value{-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none;cursor:default}.ng-select.ng-select-disabled .ng-arrow-wrapper{cursor:default}.ng-select.ng-select-filtered .ng-placeholder{display:none}.ng-select .ng-select-container{color:#333;cursor:default;display:-webkit-box;display:-ms-flexbox;display:flex;outline:0;overflow:hidden;position:relative;width:100%}.ng-select .ng-select-container .ng-value-container{display:-webkit-box;display:-ms-flexbox;display:flex;-webkit-box-flex:1;-ms-flex:1;flex:1}.ng-select .ng-select-container .ng-value-container .ng-input{opacity:0}.ng-select .ng-select-container .ng-value-container .ng-input>input{color:#333!important;-webkit-box-sizing:content-box;box-sizing:content-box;background:none;border:0;-webkit-box-shadow:none;box-shadow:none;outline:0;cursor:default;width:100%}.ng-select .ng-select-container .ng-value-container .ng-input>input::-ms-clear{display:none}.ng-select .ng-select-container .ng-value-container .ng-input>input[readonly]{-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}.ng-select.ng-select-single.ng-select-filtered .ng-select-container .ng-value-container .ng-value{visibility:hidden}.ng-select.ng-select-single .ng-select-container .ng-value-container,.ng-select.ng-select-single .ng-select-container .ng-value-container .ng-value{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.ng-select.ng-select-single .ng-select-container .ng-value-container .ng-value .ng-value-icon{display:none}.ng-select.ng-select-single .ng-select-container .ng-value-container .ng-input{position:absolute;left:0;width:100%}.ng-select.ng-select-multiple.ng-select-disabled>.ng-select-container .ng-value-container .ng-value .ng-value-icon{display:none}.ng-select.ng-select-multiple .ng-select-container .ng-value-container{-ms-flex-wrap:wrap;flex-wrap:wrap}.ng-select.ng-select-multiple .ng-select-container .ng-value-container .ng-value{white-space:nowrap}.ng-select.ng-select-multiple .ng-select-container .ng-value-container .ng-value.ng-value-disabled .ng-value-icon{display:none}.ng-select.ng-select-multiple .ng-select-container .ng-value-container .ng-value .ng-value-icon{cursor:pointer}.ng-select.ng-select-multiple .ng-select-container .ng-value-container .ng-input{-webkit-box-flex:1;-ms-flex:1;flex:1;z-index:2}.ng-select.ng-select-multiple .ng-select-container .ng-value-container .ng-placeholder{position:absolute;z-index:1}.ng-select .ng-clear-wrapper{cursor:pointer;position:relative;width:17px;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}.ng-select .ng-clear-wrapper .ng-clear{display:inline-block;font-size:18px;line-height:1}.ng-select .ng-spinner-loader{border-radius:50%;width:17px;height:17px;margin-right:5px;font-size:10px;position:relative;text-indent:-9999em;border-top:2px solid rgba(66,66,66,.2);border-right:2px solid rgba(66,66,66,.2);border-bottom:2px solid rgba(66,66,66,.2);border-left:2px solid #424242;-webkit-transform:translateZ(0);transform:translateZ(0);-webkit-animation:.8s linear infinite load8;animation:.8s linear infinite load8}.ng-select .ng-spinner-loader:after{border-radius:50%;width:17px;height:17px}@-webkit-keyframes load8{0%{-webkit-transform:rotate(0);transform:rotate(0)}100%{-webkit-transform:rotate(360deg);transform:rotate(360deg)}}@keyframes load8{0%{-webkit-transform:rotate(0);transform:rotate(0)}100%{-webkit-transform:rotate(360deg);transform:rotate(360deg)}}.ng-select .ng-arrow-wrapper{cursor:pointer;position:relative;text-align:center;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}.ng-select .ng-arrow-wrapper .ng-arrow{display:inline-block;height:0;width:0;position:relative}`],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NgSelectComponent),
      multi: true
    }
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'listbox',
    class: 'ng-select',
    '[class.ng-select-single]': '!multiple'
  }
})
export class NgSelectComponent
  implements OnDestroy, OnChanges, AfterViewInit, ControlValueAccessor {
  // inputs
  @Input() items: any[] = [];
  @Input() bindLabel: string;
  @Input() bindValue: string;
  @Input() placeholder: string;
  @Input() notFoundText: string;
  @Input() typeToSearchText: string;
  @Input() addTagText: string;
  @Input() loadingText: string;
  @Input() clearAllText: string;
  @Input() dropdownPosition: DropdownPosition = 'auto';
  @Input() appendTo: string;
  @Input() maxSelectedItems: number;
  @Input() groupBy: string;
  @Input() focusOnSelect = false;
  @Input() clearable = true;
  @Input() markFirst = false;
  @Input() loading = false;
  @Input() closeOnSelect = true;
  @Input() hideSelected = false;
  @Input() selectOnTab = false;
  @Input() bufferAmount = 4;
  @Input() virtualScroll = false;
  @Input() selectableGroup = false;
  @Input() searchFn = null;
  @Input() clearSearchOnAdd = true;
  @Input() labelForId = '';
  @Input() readOnly = false;
  @Input() disabled = false;
  @Input() env;
  @Input() @HostBinding('class.ng-select-typeahead') typeahead: Subject<string>;
  @Input() @HostBinding('class.ng-select-multiple') multiple = false;
  @Input() @HostBinding('class.ng-select-taggable') addTag:
    | boolean
    | AddTagFn = false;
  @Input() @HostBinding('class.ng-select-searchable') searchable = true;

  @Input()
  get compareWith() {
    return this._compareWith;
  }
  set compareWith(fn: CompareWithFn) {
    if (!isFunction(fn)) {
      throw Error('`compareWith` must be a function.');
    }
    this._compareWith = fn;
  }

  // output events
  @Output('blur') blurEvent = new EventEmitter();
  @Output('focus') focusEvent = new EventEmitter();
  @Output('change') changeEvent = new EventEmitter();
  @Output('open') openEvent = new EventEmitter();
  @Output('close') closeEvent = new EventEmitter();
  @Output('search') searchEvent = new EventEmitter();
  @Output('clear') clearEvent = new EventEmitter();
  @Output('add') addEvent = new EventEmitter();
  @Output('remove') removeEvent = new EventEmitter();
  @Output('enter') enterEvent = new EventEmitter();
  @Output('closedOnEnter') closedOnEnterEvent = new EventEmitter();
  @Output('scrollToEnd') scrollToEnd = new EventEmitter<{
    start: number;
    end: number;
  }>();

  // custom templates
  @ContentChild(NgOptionTemplateDirective, { read: TemplateRef })
  optionTemplate: TemplateRef<any>;
  @ContentChild(NgOptgroupTemplateDirective, { read: TemplateRef })
  optgroupTemplate: TemplateRef<any>;
  @ContentChild(NgLabelTemplateDirective, { read: TemplateRef })
  labelTemplate: TemplateRef<any>;
  @ContentChild(NgMultiLabelTemplateDirective, { read: TemplateRef })
  multiLabelTemplate: TemplateRef<any>;
  @ContentChild(NgHeaderTemplateDirective, { read: TemplateRef })
  headerTemplate: TemplateRef<any>;
  @ContentChild(NgFooterTemplateDirective, { read: TemplateRef })
  footerTemplate: TemplateRef<any>;
  @ContentChild(NgNotFoundTemplateDirective, { read: TemplateRef })
  notFoundTemplate: TemplateRef<any>;
  @ContentChild(NgTypeToSearchTemplateDirective, { read: TemplateRef })
  typeToSearchTemplate: TemplateRef<any>;
  @ContentChild(NgLoadingTextTemplateDirective, { read: TemplateRef })
  loadingTextTemplate: TemplateRef<any>;
  @ContentChild(NgTagTemplateDirective, { read: TemplateRef })
  tagTemplate: TemplateRef<any>;

  @ViewChild(forwardRef(() => NgDropdownPanelComponent))
  dropdownPanel: NgDropdownPanelComponent;
  @ContentChildren(NgOptionComponent, { descendants: true })
  ngOptions: QueryList<NgOptionComponent>;
  @ViewChild('filterInput') filterInput: ElementRef;
  @ViewChild('labelSpanRef') labelSpanRef: ElementRef;

  @HostBinding('class.ng-select-opened') get isOpened() {
    return this.isOpen;
  }
  @HostBinding('class.ng-select-disabled') get isDisabled() {
    return this.disabled;
  }
  @HostBinding('class.ng-select-filtered') get filtered() {
    return !!this.filterValue && this.searchable;
  }

  itemsList = new ItemsList(this);
  viewPortItems: NgOption[] = [];
  filterValue: string = null;
  dropdownId = newId();
  selectedItemId = 0;
  showDropdownPanel = true;
  isOpen = false;
  addItem = true;

  private _defaultLabel = 'label';
  private _primitive: boolean;
  private _focused: boolean;
  private _pressedKeys: string[] = [];
  private _compareWith: CompareWithFn;

  private readonly _destroy$ = new Subject<void>();
  private readonly _keyPress$ = new Subject<string>();
  private _onChange = (_: NgOption) => {};
  private _onTouched = () => {};

  clearItem = (item: any) => {
    this.showMessage(`clearItem:`, item);
    const option = this.selectedItems.find(x => x.value === item);
    this.unselect(option);
  };

  constructor(
    @Inject(NG_SELECT_DEFAULT_CONFIG) config: NgSelectConfig,
    @Attribute('class') public classes: string,
    private _cd: ChangeDetectorRef,
    private _console: ConsoleService,
    private _zone: NgZone,
    private _window: WindowService,
    public elementRef: ElementRef
  ) {
    this._mergeGlobalConfig(config);
  }

  get selectedItems(): NgOption[] {
    this.showMessage(`selectedItems:`, this.itemsList && this.itemsList.value);
    return this.itemsList.value;
  }

  get selectedValues() {
    this.showMessage(
      `selectedValues:`,
      this.selectedItems.map(x => x.value)
    );
    return this.selectedItems.map(x => x.value);
  }

  get hasValue() {
    this.showMessage(`hasValue:`, this.selectedItems.length > 0);
    return this.selectedItems.length > 0;
  }

  ngOnInit() {
    this._handleKeyPresses();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.multiple) {
      this.itemsList.clearSelected();
    }
    if (changes.items) {
      this._setItems(changes.items.currentValue || []);
    }
  }

  ngAfterViewInit() {
    if (this.items && this.items.length === 0) {
      this._setItemsFromNgOptions();
    }
  }

  ngOnDestroy() {
    this.showMessage(`ng-select Destoyed`);
    this._destroy$.next();
    this._destroy$.complete();
  }

  @HostListener('keydown', ['$event'])
  handleKeyDown($event: KeyboardEvent) {
    if (KeyCode[$event.which]) {
      switch ($event.which) {
        case KeyCode.ArrowDown:
          this._handleArrowDown($event);
          break;
        case KeyCode.ArrowUp:
          this._handleArrowUp($event);
          break;
        case KeyCode.Space:
          this._handleSpace($event);
          break;
        case KeyCode.Enter:
          this._handleEnter($event);
          break;
        case KeyCode.Tab:
          this._handleTab($event);
          break;
        case KeyCode.Esc:
          this.close(true);
          break;
        case KeyCode.Backspace:
          this._handleBackspace();
          break;
      }
    } else if ($event.key && $event.key.length === 1) {
      this._keyPress$.next($event.key.toLocaleLowerCase());
    }
  }

  handleMousedown($event: MouseEvent) {
    this.showMessage(`handleMousedown:`, $event);

    const target = $event.target as HTMLElement;
    if (target.tagName !== 'INPUT') {
      $event.preventDefault();
    }

    if (target.className === 'ng-clear') {
      this.handleClearClick();
      return;
    }
    if (target.className.includes('ng-arrow')) {
      this.handleArrowClick();
      return;
    }

    if (!this._focused) {
      this.focus();
    }

    if (target.className.includes('ng-value-icon')) {
      return;
    }

    if (this.searchable) {
      this.showDropdownPanel = true;
      this.open();
    } else {
      this.toggle();
    }
  }

  handleArrowClick() {
    this.showMessage(`Event: handleArrowClick`);
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  handleClearClick() {
    this.showMessage(`Event: handleClearClick`);
    if (this.hasValue) {
      this.clearModel();
    }
    this._clearSearch();
    this.focus();
    if (this._isTypeahead) {
      this.typeahead.next(null);
    }
    this.clearEvent.emit();
  }

  clearModel() {
    this.showMessage(`Event: clearModel`);
    if (!this.clearable) {
      return;
    }
    this.itemsList.clearSelected();
    this._updateNgModel();
  }

  writeValue(value: any | any[]): void {
    this.showMessage(`Event: writeValue`);
    this.itemsList.clearSelected();
    this._handleWriteValue(value);
    this._cd.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.showMessage(`registerOnChanges:`, fn);
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.showMessage(`registerOnTouched:`, fn);
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.showMessage(`setDisabledState:`, isDisabled);
    this.disabled = isDisabled;
    this._cd.markForCheck();
  }

  selectAddTag(): void {
    this.itemsList.unmarkItem();
    this.addItem = true;
  }

  unSelectAddTag(): void {
    this.addItem = false;
  }

  toggle() {
    this.showMessage(`Event: toggle`);
    if (!this.isOpen) {
      this.open();
    } else {
      this.close();
    }
  }

  open() {
    this.filterInput.nativeElement.focus();
    this.addItem = false;
    this.showMessage(`Dropdown: open`);
    if (this.disabled || this.isOpen || this.itemsList.maxItemsSelected) {
      return;
    }

    if (!this._isTypeahead && !this.addTag && this.itemsList.noItemsToSelect) {
      return;
    }
    this.isOpen = true;
    this.itemsList.unmarkItem();
    // this.itemsList.markSelectedOrDefault(this.markFirst);
    this.openEvent.emit();
    if (!this.filterValue && !this.multiple) {
      if (
        this.labelSpanRef &&
        this.labelSpanRef.nativeElement.innerHTML != undefined
      ) {
        this.filterValue = this.labelSpanRef.nativeElement.innerHTML;
      }
      this.focus();
    }
    this.detectChanges();
  }

  close(outsideClick = false) {
    this.showMessage(`Dropdown: close`);

    if (outsideClick) {
      this.selectTag(false, true);
    }

    if (!this.isOpen) {
      return;
    }

    this._clearSearch();
    this.filterInput.nativeElement.blur();
    this.isOpen = false;
    this._onTouched();
    this.closeEvent.emit();
    this._cd.markForCheck();
  }

  toggleItem(item: NgOption) {
    this.showMessage(`Toggle item:`, item);
    if (!item || item.disabled || this.disabled) {
      return;
    }

    if (this.multiple && item.selected) {
      this.unselect(item);
    } else {
      this.select(item);
    }
  }

  select(item: NgOption, newOptionValue: boolean = false) {
    this.showMessage(`Item selected:`, item);
    if (!this.multiple) {
      // this.filterValue = item.label;
    }
    if (!item.selected) {
      this.itemsList.select(item);
      if (this.clearSearchOnAdd) {
        this._clearSearch();
      }

      if (this.multiple) {
        this.addEvent.emit(item.value);
      }
      this._updateNgModel();
    }
    if (newOptionValue) {
      // this.emitEnterEvent();
    }
    this.itemsList.unmarkItem();
    if (this.closeOnSelect || this.itemsList.noItemsToSelect) {
      this.showDropdownPanel = false;
      this.close();

      /// focus the current selected value
      // setTimeout(() => {
      //     this.open();
      // });
    }
  }

  focus() {
    this.showMessage(`Event: Focus`);
    if (!this.filterInput) {
      return;
    }
    this._zone.runOutsideAngular(() => {
      this._window.setTimeout(() => {
        this.filterInput.nativeElement.focus();
      }, 5);
    });
  }

  unselect(item: NgOption) {
    this.showMessage(`Unselected item:`, item);
    this.itemsList.unselect(item);
    this._updateNgModel();
    this.removeEvent.emit(item);
  }

  selectTag(fromEnter?: boolean, fromClose?: boolean) {
    this.showMessage(`Event: selectTag`);
    let tag;
    if (!this.filterValue || !this.showDropdownPanel) {
      this.close();
      if (fromEnter) this.emitEnterEvent(true);
      this.closedOnEnterEvent.emit(true);
      return;
    }
    if (this.itemsList.isDuplicateItem(this.filterValue)) {
      this.select(this.itemsList.getDuplicateItem(this.filterValue));
      if (fromEnter) this.emitEnterEvent();
      return;
    }

    if (fromClose) {
      this.select(this.itemsList.addItem(this.filterValue, false), true);
      if (fromEnter) this.emitEnterEvent();
      return false;
    }

    if (!this.readOnly) {
      if (!(this.addTag && this.addItem)) {
        this.select(this.itemsList.addItem(this.filterValue, false), true);
        if (fromEnter) this.emitEnterEvent();
        return;
      }
      if (isFunction(this.addTag)) {
        tag = (<AddTagFn>this.addTag)(this.filterValue);
      } else {
        tag = this._primitive
          ? this.filterValue
          : { [this.bindLabel]: this.filterValue };
      }

      if (isPromise(tag)) {
        tag
          .then(item => {
            this.select(this.itemsList.addItem(item));
            if (fromEnter) this.emitEnterEvent();
            return;
          })
          .catch(err => {
            this.close();
            console.error(err);
          });
      } else if (tag) {
        this.select(this.itemsList.addItem(tag));
        if (fromEnter) this.emitEnterEvent();
      } else {
        this.close();
      }
    } else {
      this.close();
    }
  }

  showClear() {
    this.showMessage(`Event: showClear`);
    return (
      this.clearable && (this.hasValue || this.filterValue) && !this.disabled
    );
  }

  showAddTag() {
    this.showMessage(`Show add tag:`, this.addTag);
    return (
      this.addTag &&
      this.filterValue &&
      !this.itemsList.items.some(
        x => x.label.toLowerCase() === this.filterValue.toLowerCase()
      ) &&
      !this.loading &&
      !this.readOnly
    );
  }

  showNoItemsFound() {
    this.showMessage(`Show no item found`);
    const empty = this.itemsList.filteredItems.length === 0;
    return (
      ((empty && !this._isTypeahead && !this.loading) ||
        (empty && this._isTypeahead && this.filterValue && !this.loading)) &&
      !this.showAddTag()
    );
  }

  showTypeToSearch() {
    this.showMessage(`Show type to search`);
    const empty = this.itemsList.filteredItems.length === 0;
    return empty && this._isTypeahead && !this.filterValue && !this.loading;
  }

  filter() {
    this.showMessage(`Filter:`, this.filterValue);

    if (!this.filterValue && !this.multiple) {
      this.clearModel();
    }
    this.showDropdownPanel = true;
    this.open();

    if (this._isTypeahead) {
      this.typeahead.next(this.filterValue);
    } else {
      this.itemsList.filter(this.filterValue);
      this.itemsList.markSelectedOrDefault(this.markFirst);
    }
  }

  onInputFocus() {
    this.showMessage(`On input focus`);
    (<HTMLElement>this.elementRef.nativeElement).classList.add(
      'ng-select-focused'
    );
    this.focusEvent.emit(null);
    this._focused = true;
    // this.open();
  }

  onInputBlur() {
    this.showMessage(`On input blur`);
    (<HTMLElement>this.elementRef.nativeElement).classList.remove(
      'ng-select-focused'
    );
    this.blurEvent.emit(null);
    if (!this.isOpen && !this.disabled) {
      this._onTouched();
    }
    this._focused = false;
  }

  onItemHover(item: NgOption) {
    this.showMessage(`On input hover`);
    if (item.disabled) {
      return;
    }
    this.itemsList.markItem(item);
  }

  detectChanges() {
    this.showMessage(`Detect changes`);
    if (!(<any>this._cd).destroyed) {
      this._cd.detectChanges();
    }
  }

  updateDropdownPosition() {
    this.showMessage(`Update dropdown position`);
    if (this.dropdownPanel) {
      this.dropdownPanel.updateDropdownPosition();
    }
  }

  emitEnterEvent(send?: boolean) {
    if (this.closeOnSelect || send) {
      this.enterEvent.emit();
    }
  }
  private _setItems(items: any[]) {
    this.showMessage(`Set items:`, items);
    const firstItem = items[0];
    this.bindLabel = this.bindLabel || this._defaultLabel;
    this._primitive = !isObject(firstItem);
    this.itemsList.setItems(items);
    if (items.length > 0 && this.hasValue) {
      this.itemsList.mapSelectedItems();
    }

    if (this._isTypeahead || this.isOpen) {
      this.itemsList.markSelectedOrDefault(this.markFirst);
    }
  }

  private _setItemsFromNgOptions() {
    this.showMessage(`Set items from ng-options`);
    const handleNgOptions = (options: QueryList<NgOptionComponent>) => {
      this.items = options.map(option => ({
        $ngOptionValue: option.value,
        label: option.elementRef.nativeElement.innerHTML,
        disabled: option.disabled
      }));
      this.itemsList.setItems(this.items);
      if (this.hasValue) {
        this.itemsList.mapSelectedItems();
      }
      this.detectChanges();
    };

    const handleOptionChange = () => {
      this.showMessage(`Handle option change`);
      const changedOrDestroyed = merge(this.ngOptions.changes, this._destroy$);
      merge(...this.ngOptions.map(option => option.stateChange$))
        .pipe(takeUntil(changedOrDestroyed))
        .subscribe(option => {
          const item = this.itemsList.findItem(option.value);
          item.disabled = option.disabled;
          this._cd.markForCheck();
        });
    };

    this.ngOptions.changes
      .pipe(startWith(this.ngOptions), takeUntil(this._destroy$))
      .subscribe(options => {
        handleNgOptions(options);
        handleOptionChange();
      });
  }

  private _isValidWriteValue(value: any): boolean {
    this.showMessage(`Is valid write value:`, value);
    if (
      !isDefined(value) ||
      (this.multiple && value === '') ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return false;
    }

    const validateBinding = (item: any): boolean => {
      this.showMessage(`Validating binding`);
      if (isObject(item) && this.bindValue) {
        this._console.warn(
          `Binding object(${JSON.stringify(
            item
          )}) with bindValue is not allowed.`
        );
        return false;
      }
      return true;
    };

    if (this.multiple) {
      if (!Array.isArray(value)) {
        this._console.warn('Multiple select ngModel should be array.');
        return false;
      }
      return value.every(item => validateBinding(item));
    } else {
      return validateBinding(value);
    }
  }

  private _handleWriteValue(ngModel: any | any[]) {
    this.showMessage(`Handle write key`);
    if (!this._isValidWriteValue(ngModel)) {
      return;
    }

    const select = (val: any) => {
      this.showMessage(`Select:`, val);
      let item = this.itemsList.findItem(val);
      if (item) {
        this.itemsList.select(item);
        // if(!this.multiple) {
        //     this.filterValue = item.label;
        // }
      } else {
        const isValObject = isObject(val);
        const isPrimitive = !isValObject && !this.bindValue;
        if (isValObject || isPrimitive) {
          this.itemsList.select(this.itemsList.mapItem(val, null));
        } else if (this.bindValue) {
          item = {
            [this.bindLabel]: null,
            [this.bindValue]: val
          };
          this.itemsList.select(this.itemsList.mapItem(item, null));
        }
      }
    };

    if (this.multiple) {
      (<any[]>ngModel).forEach(item => select(item));
    } else {
      select(ngModel);
    }
  }

  _handleKeyPresses() {
    this.showMessage(`Handle key press`);
    if (this.searchable) {
      return;
    }

    this._keyPress$
      .pipe(
        takeUntil(this._destroy$),
        tap(letter => this._pressedKeys.push(letter)),
        debounceTime(200),
        filter(() => this._pressedKeys.length > 0),
        map(() => this._pressedKeys.join(''))
      )
      .subscribe(term => {
        const item = this.itemsList.findByLabel(term);
        if (item) {
          if (this.isOpen) {
            this.itemsList.markItem(item);
            this._cd.markForCheck();
          } else {
            this.select(item);
          }
        }
        this._pressedKeys = [];
      });
  }

  private _updateNgModel() {
    this.showMessage(`Update ng model`);
    const model = [];
    for (const item of this.selectedItems) {
      if (this.bindValue) {
        let resolvedValue = null;
        if (item.hasChildren) {
          resolvedValue = item.value[this.groupBy];
        } else {
          resolvedValue = this.itemsList.resolveNested(
            item.value,
            this.bindValue
          );
        }
        model.push(resolvedValue);
      } else {
        model.push(item.value);
      }
    }

    if (this.multiple) {
      this._onChange(model);
      this.changeEvent.emit(this.selectedItems.map(x => x.value));
      this.searchEvent.emit(this.selectedItems.map(x => x.label));
    } else {
      this._onChange(isDefined(model[0]) ? model[0] : null);
      this.changeEvent.emit(
        this.selectedItems[0] && this.selectedItems[0].value
      );
      this.searchEvent.emit(
        this.selectedItems[0] && this.selectedItems[0].label
      );
    }

    this._cd.markForCheck();
  }

  private _clearSearch() {
    this.showMessage(`Clear search`);
    // if (!this.filterValue) {
    //     return;
    // }
    this.filterValue = null;
    this.itemsList.resetItems();
  }

  private _scrollToMarked() {
    this.showMessage(`Scroll to marked`);
    if (!this.isOpen || !this.dropdownPanel) {
      return;
    }
    this.dropdownPanel.scrollInto(this.itemsList.markedItem);
  }

  private _scrollToTag() {
    this.showMessage(`Scroll to tag`);
    if (!this.isOpen || !this.dropdownPanel) {
      return;
    }
    this.dropdownPanel.scrollIntoTag();
  }

  private _handleTab($event: KeyboardEvent) {
    this.showMessage(`Handle tab`);
    if (!this.isOpen) {
      return;
    }
    if (this.selectOnTab) {
      if (this.itemsList.markedItem) {
        this.toggleItem(this.itemsList.markedItem);
        $event.preventDefault();
      } else if (this.showAddTag()) {
        this.selectTag();
        $event.preventDefault();
      } else {
        this.close();
      }
    } else {
      this.close();
    }
  }

  private _handleEnter($event: KeyboardEvent) {
    this.showMessage(`Handle enter`);
    if (this.isOpen) {
      if (this.itemsList.markedItem) {
        if (
          !this.itemsList.markedItem.label &&
          !this.itemsList.markedItem.value['name']
        ) {
          this.close();
        }
        this.toggleItem(this.itemsList.markedItem);
        this.emitEnterEvent();
      } else if (this.addTag && this.addItem) {
        this.selectTag(true);
      } else {
        this.selectTag(true);
      }
    } else if (this.itemsList.maxItemsSelected) {
      this.emitEnterEvent();
      this.filterInput.nativeElement.blur();
    } else {
      this.showDropdownPanel = true;
      this.open();
    }
    $event.preventDefault();
    $event.stopPropagation();
  }

  private _handleSpace($event: KeyboardEvent) {
    this.showMessage(`Handle space`);
    if (this.isOpen) {
      return;
    }
    this.open();
    $event.preventDefault();
  }

  private _handleArrowDown($event: KeyboardEvent) {
    this.showMessage(`Handle arrow down`);
    if (this.nextItemIsTag(+1)) {
      this.itemsList.unmarkItem();
      this._scrollToTag();
      this.open();
      this.addItem = true;
    } else {
      this.addItem = false;
      this.itemsList.markNextItem();
      this._scrollToMarked();
      this.open();
    }
    $event.preventDefault();
  }

  private _handleArrowUp($event: KeyboardEvent) {
    this.showMessage(`Handle arrow up`);
    if (!this.isOpen) {
      return;
    }

    if (this.nextItemIsTag(-1)) {
      this.itemsList.unmarkItem();
      this.addItem = true;
      this._scrollToTag();
    } else {
      this.addItem = false;
      this.itemsList.markPreviousItem();
      this._scrollToMarked();
    }
    $event.preventDefault();
  }

  private nextItemIsTag(nextStep: number): boolean {
    this.showMessage(`nextItemIsTag:`, nextStep);
    const nextIndex = this.itemsList.markedIndex + nextStep;
    return (
      this.addTag &&
      this.filterValue &&
      (nextIndex < 0 || nextIndex === this.itemsList.filteredItems.length)
    );
  }

  private _handleBackspace() {
    this.showMessage(`Handle backspace: filterValue =`, this.filterValue);
    if (this.filterValue && this.filterValue.length <= 1 && !this.multiple) {
      this.clearModel();
    }
    if (this.filterValue || !this.clearable || !this.hasValue) {
      return;
    }

    if (this.multiple) {
      this.unselect(this.itemsList.lastSelectedItem);
    } else {
      this.clearModel();
    }
  }

  private get _isTypeahead() {
    this.showMessage(`isTypeahead:`, this.typeahead);
    return this.typeahead && this.typeahead.observers.length > 0;
  }

  private showMessage(message: any, data?: any) {
    this._console.showMessage(this.env, message, data);
  }

  private _mergeGlobalConfig(config: NgSelectConfig) {
    this.notFoundText = this.notFoundText || config.notFoundText;
    this.typeToSearchText = this.typeToSearchText || config.typeToSearchText;
    this.addTagText = this.addTagText || config.addTagText;
    this.loadingText = this.loadingText || config.loadingText;
    this.clearAllText = this.clearAllText || config.clearAllText;
  }
}
